
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
// TODO: THIS LINE SHOULD BE REMOVED AFTER TESTING IE11
// import '../polyfills/intl-generic';

import {
  QueryClient,
  Hydrate,
  QueryClientProvider,
} from '@tanstack/react-query';
import cx from 'classnames';
import { Provider, useAtom } from 'jotai';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { memo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Open_Sans, Nunito_Sans } from 'next/font/google';

import FooterUpperCurve from '../components/modules/footer/footer-upper-curve';
import SelectLanguage from '../components/modules/language-selector/in-drawer';
import NextLoading from '../components/modules/next-loading';
import PageWrapper from '../components/modules/page-wrapper';
import {
  AppContextProvider,
} from '../components/providers/app-context';
import GTM from '../components/providers/gtm';
import { useErrorKey } from '../hooks/use-error-reset-key';
import { settingsModalOpen } from '../state/cart';
import '../styles/globals.css';
import { configPage, presetupConfigs } from '../utils/configPage';
import tenantConfig from '../../config/tenants';
import LazyHydrate from 'react-lazy-hydration';
//import AWF from '../components/providers/awf';


const openSans = Open_Sans({
  subsets: ['latin'],
  weight: ['300', '600'],
  display: 'swap',
})

const nunitoSans = Nunito_Sans({
  subsets: ['latin'],
  weight: ['300', '700'],
  display: 'swap',
})

import SBAuthGuard from '../components/providers/sb-auth-guard';
import GlobalAuthGuard from '../components/providers/sb-auth-guard';

// --- Dynamic Imports ---
const UserSettingsModal = dynamic(() => import('../components/modules/user-settings-modal'));
const ShippingSelectorInDrawer = dynamic(() => import('../components/modules/shipping-selector/in-drawer'));
const Footer = dynamic(() => import('../components/modules/footer'));
const Header = dynamic(() => import('../components/modules/header'));
const Alerts = dynamic(() => import('../components/modules/alerts'), { ssr: false });
const CookieConcent = dynamic(() => import('../components/modules/cookie-concent'), { ssr: false });
const Cart = dynamic(() => import('../components/modules/cart'), { ssr: false });
// const VehiclePicker = dynamic(() => import('../components/modules/v2/vehicle-picker'), { ssr: false });
const VehiclePickerAdvanced = dynamic(() => import('../components/modules/v2/vehicle-picker-advanced'), { ssr: false });
const CataloguePicker = dynamic(() => import('../components/modules/catalogue-picker'), { ssr: false });
const Menu = dynamic(() => import('../components/modules/menu'), { ssr: false });
const PrivateMenu = dynamic(() => import('../components/modules/menu/private-menu'), { ssr: false });
const Search = dynamic(() => import('../components/modules/search'), { ssr: false });
const Error505 = dynamic(() => import('../pages/500'), { ssr: false });

import {
  CookieConcentLazy,
  CartLazy,
  VehiclePickerLazy,
  CataloguePickerLazy,
  MenuLazy,
  SearchLazy,
  UsModalLazy,
  UkModalLazy,
  GlobalUserModalLazy,
  SepaModalLazy,
} from '../components/modules/lazy-modals';
import { GoogleOAuthProvider } from '@react-oauth/google';


type TMyAPP = AppProps & {
  Component: AppProps['Component'] & TPageConfig;
};

const MyApp = memo(({ Component, pageProps }: TMyAPP) => {
  const { pathname } = useRouter();
  const [isSettingModalOpen] = useAtom(settingsModalOpen);

  const componentSetup = configPage[pathname] || presetupConfigs.default;
  Component.header = componentSetup.header;
  Component.menu = componentSetup.menu;
  Component.wrapper = componentSetup.wrapper;
  Component.footerCurve = componentSetup.footerCurve;
  Component.footer = componentSetup.footer;

  if (Component.isError500) {
    return <Component />;
  }

  const fontName = tenantConfig.font.name;
  const fontFamilyString = fontName === 'Open Sans'
    ? openSans.style.fontFamily
    : fontName === 'Nunito Sans'
      ? nunitoSans.style.fontFamily
      : fontName;

  return (
    <>
      <style jsx global>{`
        html, body {
          font-family: ${fontFamilyString}, sans-serif;
        }
      `}</style>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      </Head>

      {/* <AWF /> */}
      <GTM />
      <CookieConcentLazy />
      <UsModalLazy />
      <UkModalLazy />
      <GlobalUserModalLazy />
      <SepaModalLazy />

      {isSettingModalOpen && <UserSettingsModal />}

      <Header type={Component.header || 'DEFAULT'} />
      <Alerts />
      <MenuLazy />

      <CartLazy />

      <SearchLazy />
      <ShippingSelectorInDrawer />
      <SelectLanguage />

      <VehiclePickerLazy />
      <CataloguePickerLazy />

      <div className={cx('relative', 'z-[1]', 'main-content')}>
        <NextLoading />
        <PageWrapper type={Component.wrapper}>
          <Component {...pageProps} />
        </PageWrapper>
      </div>

      <div className="footer-content">
        <LazyHydrate whenVisible>
          <FooterUpperCurve variant={Component.footerCurve} />
          <Footer type={Component.footer || 'DEFAULT'} />
        </LazyHydrate>
      </div>
    </>
  );
});

MyApp.displayName = 'MyApp';

const ErrorProtection = (props) => {
  const key = useErrorKey();

  return (
    <ErrorBoundary fallback={<Error505 />} resetKeys={[key]}>
      <MyApp {...props} />
    </ErrorBoundary>
  );
};

const AppProviders = (props) => {
  const isServiceBox =
    process.env.NEXT_PUBLIC_TENANT == '5';

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retry: false,
          },
        },
      })
  );

  const appTree = (
    <QueryClientProvider client={queryClient}>
      <AppContextProvider
        shippingLocation={props.pageProps.shippingLocation}
        detailPrices={props.pageProps.detailPrices}
        isPSA={props.pageProps.isPSA}
        isBot={props.pageProps.isBot}
      >
        <Hydrate state={props.pageProps.dehydratedState}>
          <GlobalAuthGuard>
            <ErrorProtection {...props} />
          </GlobalAuthGuard>
        </Hydrate>
      </AppContextProvider>
    </QueryClientProvider>
  );

  return (
    <Provider>
      {isServiceBox ? (
        // Service Box has no NEXT_PUBLIC_GOOGLE_CLIENT_KEY configured, and this
        // provider is what injects Google's GSI script on every page. Mounting
        // it there boots the SDK with an undefined client id, so skip it
        // entirely. The social login buttons bring their own provider where
        // they are actually used.
        appTree
      ) : (
        <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_KEY}>
          {appTree}
        </GoogleOAuthProvider>
      )}
    </Provider>
  );
};

const __Page_Next_Translate__ = AppProviders;


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: true,
// @ts-ignore
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
    });
// @ts-ignore
  